import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

function Index() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 6;

  // Currency symbol function
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋',
      'EUR': '€'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const fetchWithdrawals = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`withdrawals/?page=${page}&page_size=${itemsPerPage}`);
      console.log("API Response:", res.data);
      
      if (res.data.results !== undefined) {
        setWithdrawals(res.data.results);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else if (res.data.count !== undefined) {
        setWithdrawals(res.data.data || res.data.items || []);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else {
        const allWithdrawals = res.data;
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedWithdrawals = allWithdrawals.slice(startIndex, endIndex);
        
        setWithdrawals(paginatedWithdrawals);
        setTotalPages(Math.ceil(allWithdrawals.length / itemsPerPage));
        setTotalCount(allWithdrawals.length);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load withdrawals.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get("employees/");
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this withdrawal?")) return;
    try {
      await api.delete(`withdrawals/${id}/`);
      fetchWithdrawals(currentPage);
    } catch (err) {
      console.error(err);
      alert("Failed to delete withdrawal.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ←
      </button>
    );

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 text-sm border rounded-lg ${
            currentPage === i
              ? "bg-amber-500 text-white border-amber-500"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        →
      </button>
    );

    return buttons;
  };

  useEffect(() => {
    fetchWithdrawals(currentPage);
    fetchEmployees();
  }, [currentPage]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 text-lg">{error}</p>
    </div>
  );

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Calculate total in AFN
  const calculateTotalInAFN = () => {
    return withdrawals.reduce((sum, w) => {
      const amount = parseFloat(w.amount);
      const rate = w.exchange_rate || 1;
      return sum + (amount * rate);
    }, 0);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Withdrawals</h1>
          <p className="text-gray-600 text-sm mt-1">
            {totalCount > itemsPerPage ? `Showing ${startItem}-${endItem} of ${totalCount} withdrawals` : `Showing ${totalCount} withdrawal${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link
          to="/withdrawals/create"
          className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
        >
          <span>+</span>
          <span>Add Withdrawal</span>
        </Link>
      </div>

      {/* Withdrawals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {withdrawals.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-gray-500 text-lg mb-2">No withdrawals found</div>
            <Link
              to="/withdrawals/create"
              className="text-amber-500 hover:text-amber-600 font-medium"
            >
              Record your first withdrawal
            </Link>
          </div>
        ) : (
          withdrawals.map((w) => (
            <div key={w.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {w.employee?.name?.charAt(0)?.toUpperCase() || "E"}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {w.employee?.name || w.employee}
                      </h3>
                      <p className="text-sm text-gray-600">Employee</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-red-600">
                      -{getCurrencySymbol(w.currency || 'AFN')}{parseFloat(w.amount).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">{w.currency || "AFN"}</div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="p-6">
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Date:</span>
                    <p className="text-sm text-gray-900">
                      {w.date ? new Date(w.date).toLocaleDateString() : "Not specified"}
                    </p>
                  </div>
                  {w.currency !== 'AFN' && w.exchange_rate && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Exchange Rate:</span>
                      <p className="text-sm text-gray-900">
                        1 {w.currency} = {parseFloat(w.exchange_rate).toFixed(2)} AFN
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-700">Reason:</span>
                    <p className="text-sm text-gray-900">
                      {w.reason || "No reason provided"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">ID:</span>
                    <p className="text-sm text-gray-900">#{w.id}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/withdrawals/edit/${w.id}`}
                      className="text-blue-500 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </Link>
                    <button
                      onClick={() => handleDelete(w.id)}
                      className="text-red-500 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    Recorded
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing {startItem}-{endItem} of {totalCount} withdrawals
          </div>
          <div className="flex items-center space-x-1 flex-wrap justify-center">
            {renderPaginationButtons()}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {withdrawals.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Withdrawals (AFN)</p>
                <p className="text-2xl font-bold text-red-600">
                  ؋{calculateTotalInAFN().toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <span className="text-red-600 text-xl">💰</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(withdrawals.map(w => w.employee?.id || w.employee)).size}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-blue-600 text-xl">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Withdrawal (AFN)</p>
                <p className="text-2xl font-bold text-amber-600">
                  ؋{(calculateTotalInAFN() / withdrawals.length).toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <span className="text-amber-600 text-xl">📊</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Index;